import { PaginationResp } from '.'
import apiRequest from '../config/api'

export interface User {
  id: number
  fullname: string
  email: string
  phone_number: string
  nik: string | null
  address: string | null
  profession: string | null
  bio: string | null
  profile_picture: string | null
  role: string
  email_verified_at: string | null
  created_at: string | null
  updated_at: string | null
}

export type UserListResponse = {
  data: User[]
} & PaginationResp

export type UserDetailResponse = {
  user: User
}

// Extended User Detail Response with transactions and courses
export interface ExtendedUserDetail {
  id: number
  fullname: string
  email: string
  phone_number: string
  gender: string | null
  dob: string | null
  agency: string | null
  nik: string | null
  address: string | null
  profession: string | null
  bio: string | null
  profile_picture: string | null
  role: string
  email_verified_at: string | null
  created_at: string
  updated_at: string
}

export interface ExtendedUserTransactionCourse {
  id: number
  title: string
  category_id: string
  course_level: string
  max_student: string
  is_public: boolean
  short_description: string
  description: string
  status: string
  type: string
  start_date: string | null
  end_date: string | null
  duration: string
  image: string | null
  poster: string | null
  link_ebook: string | null
  link_group: string | null
  slug: string | null
  studied: string[]
  price: string
  discount_type: string
  discount: string
  created_at: string
  updated_at: string
}

export interface ExtendedUserTransaction {
  id: number
  order_id: string
  course_id: string
  email: string
  total: string
  notes: string | null
  type: string
  status: string
  redirect_url: string | null
  created_at: string
  updated_at: string
  course: ExtendedUserTransactionCourse
}

export interface ExtendedUserCourse {
  id: number
  title: string
  category_id: string
  course_level: string
  max_student: string
  is_public: boolean
  short_description: string
  description: string
  status: string
  type: string
  start_date: string | null
  end_date: string | null
  duration: string
  image: string | null
  poster: string | null
  link_ebook: string | null
  link_group: string | null
  slug: string | null
  studied: string[]
  price: string
  discount_type: string
  discount: string
  created_at: string
  updated_at: string
}

export interface ExtendedUserDetailResponse {
  message: string
  data: {
    user: ExtendedUserDetail
    transactions: ExtendedUserTransaction[]
    courses: ExtendedUserCourse[]
  }
}

export const getUsers = async (
  params: any,
  isAdmin?: boolean,
): Promise<UserListResponse> => {
  await apiRequest.getCsrfCookie()

  const url = isAdmin ? 'admin/users' : 'users'

  const res = await apiRequest.get(`/api/${url}`, {
    params: { limit: 10, page: 1, ...params },
  })

  return res.data.data
}

export const getUserDetail = async (
  id: string,
  isAdmin?: boolean,
): Promise<UserDetailResponse> => {
  await apiRequest.getCsrfCookie()

  const url = isAdmin ? 'admin/users' : 'users'

  const res = await apiRequest.get(`/api/${url}/${id}`)

  return res.data.data
}

export const getExtendedUserDetail = async (
  id: string,
  isAdmin?: boolean,
): Promise<ExtendedUserDetailResponse> => {
  await apiRequest.getCsrfCookie()

  const url = isAdmin ? 'admin/users' : 'users'

  const res = await apiRequest.get(`/api/${url}/${id}`)

  return res.data
}

export const putUserUpdate = async (
  id: string,
  formData: FormData,
  isAdmin?: boolean,
): Promise<UserDetailResponse> => {
  await apiRequest.getCsrfCookie()

  const url = isAdmin ? 'admin/users' : 'users'

  const res = await apiRequest.post(`/api/${url}/${id}?_method=PUT`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  return res.data
}

export const deleteUser = async (
  id: string,
  isAdmin?: boolean,
): Promise<UserDetailResponse> => {
  await apiRequest.getCsrfCookie()

  const url = isAdmin ? 'admin/users' : 'users'

  const res = await apiRequest.delete(`/api/${url}/${id}`)

  return res.data.data
}

export interface UserDashboardCourseCategory {
  id: number
  name: string
}

export interface UserDashboardCourse {
  id: number
  title: string
  type: string
  price: string
  status: string
  updated_at: string
  category_id: string
  image: string | null
  category: UserDashboardCourseCategory
}

export interface UserDashboardResponse {
  live_teaching_count: number
  cbt_count: number
  course_count: string
  course: UserDashboardCourse[]
  CBT: any[]
  live_teaching: any[]
}

export const getUserDashboard = async (): Promise<UserDashboardResponse> => {
  await apiRequest.getCsrfCookie()
  const res = await apiRequest.get('/api/user/dashboard')
  return res.data
}

export interface UserProfile {
  id: number
  fullname: string
  email: string
  phone_number: string
  gender: string | null
  dob: string | null
  agency: string | null
  nik: string | null
  address: string | null
  profession: string | null
  bio: string | null
  profile_picture: string | null
  role: string
  email_verified_at: string | null
  created_at: string | null
  updated_at: string | null
}

export interface UserProfileResponse {
  user: UserProfile
}

export const getUserProfile = async (): Promise<UserProfileResponse> => {
  await apiRequest.getCsrfCookie()
  const res = await apiRequest.get('/api/user/profile')

  // Update localStorage with the latest user data
  if (res.data && res.data.user) {
    localStorage.setItem('user', JSON.stringify(res.data.user))
    // Dispatch custom event for same-tab listeners
    window.dispatchEvent(new Event('localStorageUpdate'))
  }

  return res.data
}

export interface UserProfileEditRequest {
  fullname?: string
  gender?: string
  agency?: string
  nik?: string
  [key: string]: any
}

export interface UserProfileEditResponse {
  user: UserProfile
}

export const postUserProfile = async (
  data: UserProfileEditRequest,
): Promise<UserProfileEditResponse> => {
  await apiRequest.getCsrfCookie()
  const res = await apiRequest.post('/api/user/profile', data)
  return res.data
}

export const postUser = async (
  data: UserProfileEditRequest,
): Promise<UserProfileEditResponse> => {
  await apiRequest.getCsrfCookie()
  const res = await apiRequest.post('/api/admin/users', data)
  return res.data
}

export interface UserChangePasswordRequest {
  current_password: string
  new_password: string
  new_password_confirmation: string
}

export interface UserChangePasswordResponse {
  message: string
}

export const postUserChangePassword = async (
  data: UserChangePasswordRequest,
): Promise<UserChangePasswordResponse> => {
  await apiRequest.getCsrfCookie()
  const res = await apiRequest.post('/api/user/change-password', data)
  return res.data
}

export interface UserChangeEmailRequest {
  new_email: string
}

export interface UserChangeEmailResponse {
  message: string
}

export const postUserChangeEmail = async (
  data: UserChangeEmailRequest,
): Promise<UserChangeEmailResponse> => {
  await apiRequest.getCsrfCookie()
  const res = await apiRequest.post('/api/user/change-email', data)
  return res.data
}

export interface UserTransactionCourse {
  id: number
  title: string
  type: string
}

export interface User {
  fullname: string
}

export interface UserTransaction {
  id: number
  order_id: string
  course_id: string
  email: string
  total: string
  notes: string | null
  type: string
  status: string
  created_at: string
  updated_at: string
  course: UserTransactionCourse
  user: User
}

export interface UserTransactionDetail {
  id: number
  order_id: string
  course_id: string
  email: string
  total: string
  notes: string | null
  type: string
  status: string
  created_at: string
  updated_at: string
  course: UserTransactionCourse
  user: User
}

export interface UserTransactionsResponse {
  transactions: UserTransaction[]
}

export interface UserTransactionDetailResponse {
  data: UserTransactionDetail
}

export const getUserTransactions = async (): Promise<
  UserTransactionsResponse
> => {
  await apiRequest.getCsrfCookie()
  const res = await apiRequest.get('/api/user/transactions')
  return res.data
}

export const getUserTransactionDetail = async (
  id: string,
): Promise<UserTransactionDetailResponse> => {
  await apiRequest.getCsrfCookie()
  const res = await apiRequest.get(`/api/user/transactions/${id}`)
  return res.data
}

export interface UserProfilePictureResponse {
  message: string
  user: UserProfile
}

export const postUserProfilePicture = async (
  file: File,
): Promise<UserProfilePictureResponse> => {
  await apiRequest.getCsrfCookie()

  const formData = new FormData()
  formData.append('profile_picture', file)

  const res = await apiRequest.post('/api/user/profile-picture', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  // Update localStorage with the updated user data
  if (res.data && res.data.user) {
    localStorage.setItem('user', JSON.stringify(res.data.user))
    // Dispatch custom event for same-tab listeners
    window.dispatchEvent(new Event('localStorageUpdate'))
  }

  return res.data
}

// Purchased Courses API
export interface PurchasedCourse {
  id: number
  title: string
  category_id: string
  course_level: string
  max_student: string
  is_public: boolean
  short_description: string
  description: string
  status: string
  type: string
  start_date: string | null
  end_date: string | null
  duration: string | null
  image: string | null
  poster: string | null
  link_ebook: string | null
  link_group: string | null
  link_record: string | null
  slug: string | null
  studied: string[]
  price: string
  discount_type: string
  discount: string
  created_at: string
  updated_at: string
  questions_count?: string
  is_answered?: boolean
  latest_attempt_id?: number | null
  latest_score?: number | null
  is_passed?: boolean | null
  attempt_count?: number
  is_finished?: boolean
  max_attempt?: number
  min_passing_score?: string | null
  certificate_event_dates?: string | null
  certificate_jpl_total?: string | null
  certificate_jpl_note?: string | null
  certificate_speakers?: string | null
  certificate_materials?: Array<{ topic?: string | null; jpl?: string | null; speaker?: string | null }> | null
  certificate_location?: string | null
  certificate_downloads?: {
    user_id: number
    course_id: number
    name: string
    certificate_number?: string | null
    qr_url?: string | null
    evaluation_score?: string | null
    evaluation_passed?: boolean | null
    evaluation_passing_grade?: string | null
  } | null
}

export interface PurchasedCoursesResponse {
  message: string
  data: {
    Live_Teaching: PurchasedCourse[]
    CBT: PurchasedCourse[]
    Course: PurchasedCourse[]
  }
}

export const getPurchasedCourses = async (): Promise<
  PurchasedCoursesResponse
> => {
  await apiRequest.getCsrfCookie()
  const res = await apiRequest.get('/api/user/courses/purchased/all')
  return res.data
}

// Purchased Course Detail API
export interface PurchasedCourseDetail {
  id: number
  title: string
  category_id: string
  course_level: string
  max_student: string
  is_public: boolean
  short_description: string
  description: string
  status: string
  type: string
  start_date: string | null
  end_date: string | null
  duration: string | null
  image: string | null
  poster: string | null
  link_ebook: string | null
  link_group: string | null
  link_record: string | null
  slug: string | null
  studied: string[]
  price: string
  certificate_downloads: {
    user_id: number
    course_id: number
    name: string
    certificate_number?: string | null
    qr_url?: string | null
    evaluation_score?: string | null
    evaluation_passed?: boolean | null
    evaluation_passing_grade?: string | null
  } | null
  certificate_event_dates?: string | null
  certificate_jpl_total?: string | null
  certificate_jpl_note?: string | null
  certificate_speakers?: string | null
  certificate_materials?: Array<{ topic?: string | null; jpl?: string | null; speaker?: string | null }> | null
  certificate_location?: string | null
  discount_type: string
  discount: string
  created_at: string
  updated_at: string
  questions_count: string
  is_answered?: boolean
  is_finished?: boolean
  student_count: string
  category: {
    id: number
    name: string
    created_at: string
    updated_at: string
  }
  topics: Array<{
    id: number
    course_id: string
    name: string
    created_at: string
    updated_at: string
    lessons: Array<{
      id: number
      topic_id: string
      name: string
      video_link: string
      description: string
      is_premium: boolean
      created_at: string
      updated_at: string
      is_watched: boolean
    }>
  }>
  cross_sells: any[]
  benefits: Array<{
    id: number
    icon: string
    name: string
    created_at: string
    updated_at: string
    pivot: {
      course_id: string
      benefit_id: string
    }
  }>
  questions: any[]
}

export interface PurchasedCourseDetailResponse {
  message: string
  data: PurchasedCourseDetail
}

export const getPurchasedCourseDetail = async (
  id: string,
): Promise<PurchasedCourseDetailResponse> => {
  await apiRequest.getCsrfCookie()
  const res = await apiRequest.get(`/api/user/courses/login/${id}`)
  return res.data
}

// Certificate Download Analytics API
export interface CertificateDownloadRequest {
  course_id: number
  name: string
  evaluation_score?: string | null
  evaluation_passed?: boolean | null
  evaluation_passing_grade?: string | null
}

export interface CertificateDownloadResponse {
  message: string
  data?: {
    id: number
    user_id: number
    course_id: number
    name: string
    certificate_number?: string | null
    qr_url?: string | null
    evaluation_score?: string | null
    evaluation_passed?: boolean | null
    evaluation_passing_grade?: string | null
    created_at?: string
  }
}

export const postCertificateDownloadAnalytics = async (
  data: CertificateDownloadRequest,
): Promise<CertificateDownloadResponse> => {
  await apiRequest.getCsrfCookie()
  const res = await apiRequest.post('/api/user/certificate/download', data)
  return res.data
}
